import { type EventPhoto, type File as KalosFile } from '@kalos/kalos-rpc';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { getMimeType } from '../../tools/helpers';
import { fetchS3FileRecordAsDataUrl } from '../../utils/s3FileDataUrl';
import { useEventPhotoBatchGetQuery } from './useEventPhotoQuery';
import { useFilesBatchQuery } from './useFileQuery';

/**
 * Which "shown to the customer" flag a gallery is managing. Each destination a
 * photo can be published to has its own column, and the caller picks one — see
 * `visibilityFlag` below for why this is explicit rather than derived.
 */
export type PhotoVisibilityFlag =
  | 'includeInProposal'
  | 'includeInInvoice'
  | 'includeInServiceChannel';

export type EventPhotoGalleryPreviewRow = {
  eventPhotoId: number;
  fileId: number;
  serviceItemId: number;
  dataUrl: string | null;
  includeInProposal: boolean;
  includeInInvoice: boolean;
  includeInServiceChannel: boolean;
  file: Pick<KalosFile, 'id' | 'name' | 'bucket' | 'mimeType'>;
};

/**
 * Batch-get EventPhoto rows for a proposal/invoice filter, resolve File records, and download
 * data URLs for gallery thumbnails. Shared by ProposalInvoicePhotoSelector and walk-through evidence.
 */
export function useEventPhotoGalleryPreviews({
  filter,
  enabled,
  /** When true, keep a row for every event photo with file metadata even if preview download fails. */
  keepRowsWithoutPreview,
  /**
   * When true, return every active photo for the filter (both shown and hidden) so the editor can
   * render hidden ones greyed and toggle them. Read-only surfaces omit this to show only the
   * photos included on the proposal/invoice.
   */
  includeHidden = false,
  visibilityFlag,
  imagePreviewsOnly = false,
}: {
  filter: Partial<EventPhoto>;
  enabled: boolean;
  keepRowsWithoutPreview: boolean;
  includeHidden?: boolean;
  /** When true, only image files are downloaded for a thumbnail; other files (PDFs) get a null dataUrl. */
  imagePreviewsOnly?: boolean;
  /**
   * The flag this gallery manages. Defaults to whatever the filter implies —
   * quoteDocumentId means proposal, invoiceId means invoice — which keeps every
   * existing caller unchanged.
   *
   * ServiceChannel needs it stated explicitly, because its photos are simply the
   * job's photos: the filter is `{ eventId }` with no document to infer from. So
   * the destination cannot be derived, only declared.
   */
  visibilityFlag?: PhotoVisibilityFlag;
}) {
  const isProposalContext = !!filter.quoteDocumentId;
  const isInvoiceContext = !!filter.invoiceId;
  const isFilterOrderContext = !!filter.filterOrderId;
  // An event-scoped gallery is only meaningful with an explicit flag; without one
  // there is no way to know which destination it is managing.
  const isEventScoped = !!visibilityFlag && !!filter.eventId;
  const flag: PhotoVisibilityFlag =
    visibilityFlag ?? (isInvoiceContext ? 'includeInInvoice' : 'includeInProposal');
  const queryEnabled =
    enabled && (isProposalContext || isInvoiceContext || isFilterOrderContext || isEventScoped);

  const eventPhotoQuery = useEventPhotoBatchGetQuery({
    filter: {
      ...filter,
      isActive: true,
      withoutLimit: true,
      // Read-only surfaces show only what is published; editors ask for hidden
      // ones too so they can render them greyed and toggle them back.
      ...(!includeHidden && { [flag]: true }),
    },
    enabled: queryEnabled,
  });

  const fileIds = useMemo(
    () => eventPhotoQuery.data?.results?.map((photo) => photo.fileId).filter((id) => id > 0) ?? [],
    [eventPhotoQuery.data?.results],
  );

  const filesQuery = useFilesBatchQuery({
    fileIds,
    enabled: queryEnabled && fileIds.length > 0,
  });

  const [rows, setRows] = useState<EventPhotoGalleryPreviewRow[]>([]);
  const [isDownloadingPreviews, setIsDownloadingPreviews] = useState(false);

  const loadRows = useCallback(async () => {
    const results = eventPhotoQuery.data?.results;
    if (!results?.length) {
      setRows([]);
      return;
    }

    const files = filesQuery.data;
    if (!files?.length) {
      setRows([]);
      return;
    }

    const fileMap = new Map<number, KalosFile>();
    files.forEach((f) => {
      if (f?.id) {
        fileMap.set(f.id, f);
      }
    });

    setIsDownloadingPreviews(true);
    const photoPromises = results.map(async (photo) => {
      const fullFile = fileMap.get(photo.fileId);
      if (!fullFile?.name || !fullFile.bucket) {
        return null;
      }

      const mimeType = fullFile.mimeType || getMimeType(fullFile.name);
      const dataUrl =
        imagePreviewsOnly && !mimeType.startsWith('image/')
          ? null
          : await fetchS3FileRecordAsDataUrl({
              name: fullFile.name,
              bucket: fullFile.bucket,
            });

      if (!keepRowsWithoutPreview && !dataUrl) {
        return null;
      }

      return {
        eventPhotoId: photo.id,
        fileId: photo.fileId,
        serviceItemId: photo.serviceItemId,
        dataUrl: dataUrl ?? null,
        includeInProposal: photo.includeInProposal,
        includeInInvoice: photo.includeInInvoice,
        includeInServiceChannel: photo.includeInServiceChannel,
        file: {
          id: fullFile.id,
          name: fullFile.name,
          bucket: fullFile.bucket,
          mimeType,
        },
      };
    });

    const resolved = await Promise.all(photoPromises);
    setRows(resolved.filter((r): r is EventPhotoGalleryPreviewRow => r !== null));
    setIsDownloadingPreviews(false);
  }, [eventPhotoQuery.data?.results, filesQuery.data, keepRowsWithoutPreview, imagePreviewsOnly]);

  useEffect(() => {
    if (!queryEnabled) {
      setRows([]);
      setIsDownloadingPreviews(false);
      return;
    }

    if (!eventPhotoQuery.isSuccess) {
      return;
    }

    const results = eventPhotoQuery.data?.results;
    if (!results?.length) {
      setRows([]);
      setIsDownloadingPreviews(false);
      return;
    }

    if (!filesQuery.isSuccess) {
      return;
    }

    void loadRows();
  }, [
    queryEnabled,
    eventPhotoQuery.isSuccess,
    eventPhotoQuery.data?.results,
    filesQuery.isSuccess,
    filesQuery.data,
    loadRows,
  ]);

  const removeRowsByEventPhotoId = useCallback((eventPhotoIds: number[]) => {
    const idSet = new Set(eventPhotoIds);
    setRows((prev) => prev.filter((r) => !idSet.has(r.eventPhotoId)));
  }, []);

  /** Patch a row's include flags locally for optimistic UI without re-downloading previews. */
  const setRowInclude = useCallback(
    (
      eventPhotoId: number,
      patch: Partial<Pick<EventPhotoGalleryPreviewRow, PhotoVisibilityFlag>>,
    ) => {
      setRows((prev) =>
        prev.map((r) => (r.eventPhotoId === eventPhotoId ? { ...r, ...patch } : r)),
      );
    },
    [],
  );

  const refetchQueries = useCallback(async () => {
    await eventPhotoQuery.refetch();
    await filesQuery.refetch();
  }, [eventPhotoQuery, filesQuery]);

  const isLoading =
    (queryEnabled && (eventPhotoQuery.isLoading || (fileIds.length > 0 && filesQuery.isLoading))) ||
    isDownloadingPreviews;

  return {
    rows,
    isLoading,
    isDownloadingPreviews,
    fileIds,
    eventPhotoQuery,
    filesQuery,
    refetchQueries,
    removeRowsByEventPhotoId,
    setRowInclude,
  };
}
